import {
  Modal,
  ModalVariant,
  FormSelect,
  FormSelectOption,
  ActionGroup,
  Flex,
  Form,
  FormGroup,
  TextInput,
  Grid,
  GridItem,
  TextArea,
  FlexItem,
  Divider,
  Alert,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useMemo, useState, useEffect } from 'react'

import {
  getMedicationRouteLabelFromTypeId,
  getMedicationDosageLabelFromTypeId,
  getMedicationRecordStatusLabelFromId,
} from '@app/domain/patient-medication-history/patient-medication-history.helpers'
import type {
  MedicationRoute,
  MedicationDosageUnit,
  MedicationFrequencyUnit,
  PatientMedication,
  PatientMedicationRecord,
  PatientMedicationRecordEntry,
  PatientMedicationDosage,
  PatientMedicationFrequency,
  PatientMedicationFrequencyUnitId,
  PatientMedicationIndications,
  PatientMedicationPrescriber,
  PatientMedicationComments,
  PatientMedicationRecommendations} from '@app/domain/patient-medication-history/patient-medication-history.interface';
import {
  PatientMedicationRecordEntryStatus
} from '@app/domain/patient-medication-history/patient-medication-history.interface'
import { hasContent } from '@app/lib/string'
import { ValidationStatusId } from '@app/lib/validators'
import { SmallButton } from '@app/utils/styles/small-button'

interface Props {
  conflictingError: ValidationStatusId
  currentRecordEntry: Partial<PatientMedicationRecordEntry>
  editRecordEntry: (recordEntryValues: PatientMedicationRecordEntry) => void
  isModalOpen: boolean
  isExistingRecordEntry: boolean
  medicationDosageUnits: MedicationDosageUnit[]
  medicationFrequencyUnits: MedicationFrequencyUnit[]
  medicationRecordEntries: PatientMedicationRecordEntry[]
  medicationRoutes: MedicationRoute[]
  onCancel: () => void
  selectedMedication: PatientMedication
  selectedRecord: PatientMedicationRecord
}

export const EditRecordEntryModal = function ({
  conflictingError,
  currentRecordEntry,
  editRecordEntry,
  isModalOpen,
  isExistingRecordEntry,
  medicationDosageUnits,
  medicationFrequencyUnits,
  medicationRoutes,
  onCancel,
  selectedMedication,
  selectedRecord,
}: Props) {
  const defaultSelectType = 'Please Choose'
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [recordStatus, setRecordStatus] = useState<
    PatientMedicationRecordEntryStatus
  >(PatientMedicationRecordEntryStatus.Active)
  const [indication, setIndications] = useState<PatientMedicationIndications>(
    null,
  )
  const [dosage, setDosage] = useState<PatientMedicationDosage>(null)
  const [frequency, setFrequency] = useState<PatientMedicationFrequency>(null)
  const [frequencyUnitId, setFrequencyUnitId] = useState<
    PatientMedicationFrequencyUnitId
  >(null)
  const [prescriber, setPrescriber] = useState<PatientMedicationPrescriber>(
    null,
  )
  const [comments, setComments] = useState<PatientMedicationComments>(null)
  const [recommendations, setRecommendations] = useState<
    PatientMedicationRecommendations
  >(null)

  const [dosageValidation, setDosageValidation] = useState<ValidationStatusId>(
    ValidationStatusId.Default,
  )
  const [frequencyValidation, setFrequencyValidation] = useState<
    ValidationStatusId
  >(ValidationStatusId.Default)
  const [frequencyUnitValidation, setFrequencyUnitValidation] = useState<
    ValidationStatusId
  >(ValidationStatusId.Default)

  useEffect(() => {
    if (currentRecordEntry) {
      setRecordStatus(
        currentRecordEntry?.status ?? PatientMedicationRecordEntryStatus.Active,
      )
      setIndications(currentRecordEntry.indications)
      setDosage(currentRecordEntry.dosage)
      setFrequency(currentRecordEntry.frequency)
      setFrequencyUnitId(currentRecordEntry.frequencyUnitId)
      setPrescriber(currentRecordEntry.prescriber)
      setComments(currentRecordEntry.comments)
      setRecommendations(currentRecordEntry.recommendations)
    }
  }, [currentRecordEntry, isExistingRecordEntry])

  const modalTitle: string = useMemo(() => {
    return `${selectedMedication.name} (${getMedicationRouteLabelFromTypeId(
      selectedMedication.medicationRouteId,
      medicationRoutes,
    )}) ${selectedRecord.activeDate}
      `
  }, [
    medicationRoutes,
    selectedMedication.medicationRouteId,
    selectedMedication.name,
    selectedRecord.activeDate,
  ])

  const isDosageValid = hasContent(dosage)
  const isFrequencyValid = !Number.isNaN(frequency) && frequency > 0

  const isFormValid = useMemo(() => {
    if (
      isFormDirty &&
      recordStatus === PatientMedicationRecordEntryStatus.Active
    ) {
      setDosageValidation(
        isDosageValid ? ValidationStatusId.Default : ValidationStatusId.Error,
      )
      setFrequencyValidation(
        isFrequencyValid
          ? ValidationStatusId.Default
          : ValidationStatusId.Error,
      )
      setFrequencyUnitValidation(
        hasContent(frequencyUnitId) && frequencyUnitId !== defaultSelectType
          ? ValidationStatusId.Default
          : ValidationStatusId.Error,
      )
    }
    return (
      isDosageValid &&
      isFrequencyValid &&
      hasContent(frequencyUnitId) &&
      frequencyUnitId !== defaultSelectType
    )
  }, [
    isFormDirty,
    recordStatus,
    isDosageValid,
    frequencyUnitId,
    isFrequencyValid,
  ])

  const handleStatusChange = useCallback((recordStatus) => {
    setRecordStatus(recordStatus)
    if (recordStatus === PatientMedicationRecordEntryStatus.Discontinued) {
      setIndications('')
      setDosage('')
      setFrequency(0)
      setFrequencyUnitId('')
      setPrescriber('')
    }
  }, [])

  const handleTextOnBlur = useCallback(() => {
    setIsFormDirty(true)
  }, [])

  const handleSetFrequency = useCallback((frequency: string) => {
    const numberValue = Number.parseInt(frequency)
    if (!Number.isNaN(numberValue) && numberValue > 0) {
      setFrequency(numberValue)
      setFrequencyValidation(ValidationStatusId.Default)
    } else {
      setFrequency(undefined)
      setFrequencyValidation(ValidationStatusId.Error)
    }
  }, [])

  const resetForm = useCallback(() => {
    setIsFormDirty(false)
    setIndications('')
    setDosage('')
    setFrequency(0)
    setFrequencyUnitId('')
    setPrescriber('')
    setComments('')
    setRecommendations('')
    setDosageValidation(ValidationStatusId.Default)
    setFrequencyValidation(ValidationStatusId.Default)
    setFrequencyUnitValidation(ValidationStatusId.Default)
  }, [])

  const onSubmit = useCallback(() => {
    setIsFormDirty(true)

    const recordEntryValues: PatientMedicationRecordEntry = {
      patientMedicationId: selectedMedication.id,
      patientMedicationRecordId: selectedRecord.id,
      status: PatientMedicationRecordEntryStatus.Active,
      dosage: dosage,
      frequency: frequency,
      frequencyUnitId: frequencyUnitId,
      indications: indication,
      prescriber: prescriber,
      comments: comments,
      recommendations: recommendations,
    }

    if (
      recordStatus === PatientMedicationRecordEntryStatus.Active &&
      isFormValid
    ) {
      editRecordEntry(recordEntryValues)
      resetForm()
      onCancel()
    } else if (
      recordStatus === PatientMedicationRecordEntryStatus.Discontinued
    ) {
      const discontinuedRecordEntryValues = {
        ...recordEntryValues,
        status: PatientMedicationRecordEntryStatus.Discontinued,
        dosage: null,
        frequency: null,
        frequencyUnitId: null,
        indications: null,
        prescriber: null,
      }
      editRecordEntry(discontinuedRecordEntryValues)
      resetForm()
      onCancel()
    }
  }, [
    comments,
    dosage,
    editRecordEntry,
    frequency,
    frequencyUnitId,
    indication,
    isFormValid,
    onCancel,
    prescriber,
    recommendations,
    recordStatus,
    resetForm,
    selectedMedication.id,
    selectedRecord.id,
  ])

  const handleCloseModal = useCallback(() => {
    resetForm()
    onCancel()
  }, [onCancel, resetForm])

  return (
    <Modal
      variant={ModalVariant.medium}
      isOpen={isModalOpen}
      onClose={handleCloseModal}
    >
      {conflictingError === ValidationStatusId.Warning ? (
        <>
          <Alert
            variant="warning"
            title="There are duplicate medication record entries in the database. Please contact your administrator."
          />
          <br />
        </>
      ) : null}
      <h1>Medication History Entry</h1>
      <h1>{modalTitle}</h1>
      <Form>
        <Flex>
          <FlexItem>
            <Grid hasGutter md={6} style={{ marginTop: 5 }}>
              <FormGroup label="State" isRequired fieldId="state">
                <FormSelect
                  value={recordStatus}
                  isRequired
                  onChange={handleStatusChange}
                  onBlur={handleTextOnBlur}
                >
                  {Object.entries(PatientMedicationRecordEntryStatus).map(
                    ([key, val]) => (
                      <FormSelectOption
                        key={key}
                        value={PatientMedicationRecordEntryStatus[key]}
                        label={getMedicationRecordStatusLabelFromId(val)}
                      />
                    ),
                  )}
                </FormSelect>
              </FormGroup>

              <GridItem span={12}>
                <FormGroup label="Indications" fieldId="indications">
                  <TextArea
                    type="text"
                    id="indications"
                    name="indications"
                    aria-describedby="indications"
                    value={indication}
                    onChange={setIndications}
                    onBlur={handleTextOnBlur}
                    isDisabled={
                      recordStatus ===
                      PatientMedicationRecordEntryStatus.Discontinued
                    }
                  />
                </FormGroup>
              </GridItem>

              <FormGroup
                label="Dosage"
                isRequired
                fieldId="Dosage"
                validated={dosageValidation}
                helperTextInvalid="Dosage is required."
              >
                <TextInput
                  isRequired
                  type="text"
                  id="dosage"
                  name="dosage"
                  value={dosage}
                  onChange={setDosage}
                  onBlur={handleTextOnBlur}
                  placeholder="0"
                  isDisabled={
                    recordStatus ===
                    PatientMedicationRecordEntryStatus.Discontinued
                  }
                />
              </FormGroup>
              <FormGroup label="Unit" fieldId="DosageUnit">
                <TextInput
                  readOnlyVariant="plain"
                  type="text"
                  id="dosage"
                  name="dosage"
                  value={getMedicationDosageLabelFromTypeId(
                    selectedMedication.medicationDosageUnitId,
                    medicationDosageUnits,
                  )}
                />
              </FormGroup>
            </Grid>
            <Grid hasGutter md={6} style={{ marginTop: 5 }}>
              <FormGroup
                label="Frequency"
                isRequired
                fieldId="frequency"
                validated={frequencyValidation}
                helperTextInvalid="Frequency is required."
              >
                <TextInput
                  isRequired
                  type="text"
                  id="frequency"
                  name="frequency"
                  value={frequency}
                  onChange={handleSetFrequency}
                  placeholder="0"
                  isDisabled={
                    recordStatus ===
                    PatientMedicationRecordEntryStatus.Discontinued
                  }
                />
              </FormGroup>
              <FormGroup
                label=" "
                isRequired
                fieldId="frequencyUnit"
                validated={frequencyUnitValidation}
                helperTextInvalid="Frequency unit required."
              >
                <FormSelect
                  isRequired
                  value={frequencyUnitId}
                  onChange={setFrequencyUnitId}
                  onBlur={handleTextOnBlur}
                  isDisabled={
                    recordStatus ===
                    PatientMedicationRecordEntryStatus.Discontinued
                  }
                >
                  <FormSelectOption
                    key={0}
                    value={null}
                    label={defaultSelectType}
                  />
                  {medicationFrequencyUnits.map((frequencyUnit) => (
                    <FormSelectOption
                      key={frequencyUnit.id}
                      value={frequencyUnit.id}
                      label={frequencyUnit.name}
                    />
                  ))}
                </FormSelect>
              </FormGroup>

              <GridItem span={12}>
                <FormGroup label="Prescriber" fieldId="prescriber">
                  <TextInput
                    type="text"
                    id="prescriber"
                    name="prescriber"
                    aria-describedby="prescriber"
                    value={prescriber}
                    onChange={setPrescriber}
                    onBlur={handleTextOnBlur}
                    isDisabled={
                      recordStatus ===
                      PatientMedicationRecordEntryStatus.Discontinued
                    }
                  />
                </FormGroup>
              </GridItem>
            </Grid>
          </FlexItem>
          <Divider
            orientation={{
              default: 'vertical',
            }}
          />

          <FlexItem>
            <FormGroup label="Comments" fieldId="comments">
              <TextArea
                type="text"
                id="comments"
                name="comments"
                aria-describedby="comments"
                value={comments}
                onChange={setComments}
                resizeOrientation="vertical"
                rows={6}
              />
            </FormGroup>

            <FormGroup label="Recommendations" fieldId="recommendations">
              <TextArea
                type="text"
                id="recommendations"
                name="recommendations"
                aria-describedby="recommendations"
                value={recommendations}
                onChange={setRecommendations}
                resizeOrientation="vertical"
                rows={6}
              />
            </FormGroup>
          </FlexItem>
        </Flex>
      </Form>

      <ActionGroup style={{ float: 'right', marginTop: 10 }}>
        <Flex>
          <SmallButton
            style={{ marginRight: 10 }}
            key="clear"
            variant="secondary"
            isDanger
            onClick={resetForm}
          >
            Clear
          </SmallButton>

          <SmallButton
            style={{ marginRight: 5 }}
            key="cancel"
            variant="secondary"
            onClick={handleCloseModal}
          >
            Cancel
          </SmallButton>
          <SmallButton
            key="confirm"
            variant="primary"
            onClick={onSubmit}
            isDisabled={
              recordStatus === PatientMedicationRecordEntryStatus.Active &&
              !isFormValid
            }
          >
            Save
          </SmallButton>
        </Flex>
      </ActionGroup>
    </Modal>
  )
}
